import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import ConditionReport from '@sm360/condition-report';
import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    AccountService,
    EVENTS,
    ShiftDigitalService,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    CONSTANT as COMMONS_CONSTANT,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { Vehicle, Quote, TradeInsService } from '@sm360/widget-commons-vehicle';
import { logger } from '@sm360/logger';

import 'trade-in-360/components/InstantEstimateWrapper/Steps/OfficialEvaluation/OfficialEvaluation.scss';
import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import VehicleTypeLabel from 'trade-in-360/components/InstantEstimateWrapper/Components/VehicleTypeLabel/VehicleTypeLabel.jsx';
import ConfirmationMessageTrade360 from 'trade-in-360/components/InstantEstimateWrapper/Components/ConfirmationMessageTrade360/ConfirmationMessageTrade360.jsx';
import TradeInCta from 'trade-in-360/components/TradeInCta/TradeInCta.jsx';
import ErrorMessage from 'trade-in-360/components/ErrorMessage/ErrorMessage.jsx';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import CONFIRMATION_TYPE from '../../../../constants/confirmationType.js';

const { DeskingConditionForm, QuestionsListHelpers, Models } = ConditionReport;
const { OFFICIAL_FORM_STATUS } = COMMONS_CONSTANT || {};

const SECTION_LABEL = {
    CAR_STORY: 'history',
    SUSTAINED_DAMAGE: 'susDamage',
    REPAIRS_REQUIRED: 'repairsRequired',
    REGISTRATION: 'registration',
    ADDITIONAL_DISCLOSURES: 'addDisclosures',
};

const OfficialEvaluation = ({
    tradeInVehicle,
    accountTradeInVehicle,
    quote,
    onPreviousStep,
    onNextStep,
    closeText,
    onClose,
    onChange,
    onMakeAppointment,
    appointmentDate,
    config,
}) => {
    const {
        fieldsLocked,
        setFieldsLocked,
        setIsOfficialTradeIn,
        setNewRequest,
        isViewOnly,
        isModified,
        setIsModified,
        setOfficialEdit,
    } = useContext(NewTradeInContext);
    const [controlledAccountTradeInVehicle, setControlledAccountTradeInVehicle] = useState(accountTradeInVehicle);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [allCompleted, setAllCompleted] = useState(false);

    const dispatchTradeInUpdatedEvent = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_UPDATED));
        }
    };

    const saveAppointmentDate = async () => {
        try {
            const updatedTradeIn = controlledAccountTradeInVehicle.set('appointmentDate', appointmentDate);
            await TradeInService.saveTradeInNew(updatedTradeIn, quote, true, config);
            dispatchTradeInUpdatedEvent();
        } catch (e) {
            // TODO: Better error handling
            logger.error('Error saving appointment date', e);
        }
    };

    useEffect(
        () => {
            if (!isModified) setControlledAccountTradeInVehicle(accountTradeInVehicle);
        },
        [accountTradeInVehicle, saved]
    );

    useEffect(() => {
        if (!isViewOnly) {
            setIsModified(false);
            setNewRequest(false);
            setIsOfficialTradeIn(true);
            setOfficialEdit(false);
        }
        // Lock all previous screens fields
        if (!fieldsLocked) {
            setFieldsLocked(true);
        }

        AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN());
    }, []);

    /**
     * Save an appointment date if:
     * - Already have tradeIn into account,
     * - The appointment date into account tradeIn is not already set,
     * - And have an appointmentdate as props
     */
    useEffect(
        () => {
            if (
                controlledAccountTradeInVehicle?.id &&
                !controlledAccountTradeInVehicle?.appointmentDate &&
                appointmentDate
            ) {
                saveAppointmentDate();
            }
        },
        [controlledAccountTradeInVehicle]
    );

    /** Return if official evaluation status is completed or not
     * @param {*} conditionForm - conditionForm questions on save button
     * @returns boolean
     */
    const isEvaluationCompleted = (conditionForm) => {
        const officialEvaluationStatus = QuestionsListHelpers.getQuestionnaireProgressStatus(
            conditionForm || controlledAccountTradeInVehicle?.evaluationQuestion || {},
            Models.DeskingQuestionsModel
        );
        return officialEvaluationStatus === OFFICIAL_FORM_STATUS.ALL_COMPLETED;
    };

    const getVehicleTradeIn = () => {
        if (controlledAccountTradeInVehicle?.id) {
            return Promise.resolve(controlledAccountTradeInVehicle);
        }
        return TradeInsService.loadTradeInVehicle()
            .then((tradeIn) => {
                if (!tradeIn) {
                    return Promise.reject();
                }
                const vehicleTradein = {
                    tradeId: tradeIn.id,
                    vehicleId: tradeIn.vehicleId,
                    make: tradeIn.make,
                    model: tradeIn.model,
                    modelTrim: tradeIn.trim,
                    year: tradeIn.year,
                    odometer: tradeIn.kilometers,
                    retail: tradeIn.estimation.amount,
                    amount: tradeIn.estimation.amount,
                    officialAmount: tradeIn.evaluatorValues.estimatedValue,
                    officialAmountExp: Date.parse(tradeIn.evaluatorValues.expirationDate) || 0,
                    vin: tradeIn.vin,
                    appointmentDate: tradeIn.appointmentDate,
                    conditionForm: tradeIn.conditionForm,
                    creationDate: tradeIn.creationDate,
                };

                const tradeInModel = {
                    ...tradeIn,
                    minAmount: tradeIn.estimation.low,
                    maxAmount: tradeIn.estimation.high,
                    appointmentDate: tradeIn.appointmentDate,
                    evaluationQuestion: tradeIn.conditionForm,
                    tradeVehicleQuestions: tradeIn.tradeVehicleQuestions, // dilawri questions
                    vehicle: vehicleTradein,
                    creationDate: tradeIn.creationDate,
                    officialAmount: tradeIn.evaluatorValues.estimatedValue,
                    officialAmountExp: Date.parse(tradeIn.evaluatorValues.expirationDate) || 0,
                };
                const vehicle = new Vehicle.VehicleModel(tradeInModel.vehicle);
                return new Vehicle.TradeInVehicleModel({ ...tradeInModel, vehicle });
            })
            .then((ctrlAccTradeInVehicle) => {
                if (!ctrlAccTradeInVehicle?.id) {
                    return Promise.reject();
                }
                return ctrlAccTradeInVehicle;
            });
    };

    const handleReportSave = ({ conditionForm }) => {
        if (!AccountService.isAuthenticated()) {
            // Return a rejected promise to DeskingConditionForm
            setError(true);
            return Promise.reject();
        }
        return getVehicleTradeIn().then((ctrlAccTradeInVehicle) => {
            setLoading(true);
            setError(false);
            const tradeInToSave = ctrlAccTradeInVehicle.set('evaluationQuestion', conditionForm);
            return TradeInService.saveTradeInNew(tradeInToSave, quote, true, config)
                .then(() => {
                    setIsModified(false);
                    setSaved(true);
                    // To check if evaluation status is completed and if send SD event
                    if (isEvaluationCompleted(conditionForm)) {
                        ShiftDigitalService.updateDrTradeinFinish(quote?.id, null, tradeInVehicle);
                        AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_THANKS());
                    }
                    // Allow to close the confirmation modal without warning if appointemt has been already scheduled
                    onNextStep(!!controlledAccountTradeInVehicle?.appointmentDate);
                })
                .catch((e) => {
                    setError(true);
                    logger.error('Error saving condition questions: ', e);
                })
                .finally(() => setLoading(false));
        });
    };

    const tradeVehicleName = LocaleService.t('TRADE_IN.VEHICLE_NAME', {
        year: controlledAccountTradeInVehicle?.year || tradeInVehicle.year,
        make: controlledAccountTradeInVehicle?.make || tradeInVehicle.make,
        model: controlledAccountTradeInVehicle?.model || tradeInVehicle.model,
        modelTrim: controlledAccountTradeInVehicle?.modelTrim || tradeInVehicle.trim,
        modelTrimStyle: '',
    });
    const quoteVehicleInfo = quote?.vehicle?.getYearMakeModelTrim() || '';

    // If a valid official amount is provided by the salesperson, prevent user from modifying the questionnaire
    const readOnly = isViewOnly || controlledAccountTradeInVehicle.officialAmount > 0;

    const confirmationTitle = isEvaluationCompleted()
        ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.OFFICIAL_EVALUATION.CONFIRMATION_TITLE')
        : LocaleService.t('TRADE_IN.SECTIONS_CONTENT.OFFICIAL_EVALUATION.UNCOMPLETED_CONFIRMATION_TITLE');
    const confirmationDescription = isEvaluationCompleted() ? (
        LocaleService.t('TRADE_IN.SECTIONS_CONTENT.OFFICIAL_EVALUATION.CONFIRMATION_DESC')
    ) : (
        <div>
            {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.OFFICIAL_EVALUATION.UNCOMPLETED_CONFIRMATION_DESC_TEXT')}
            <a className="link" onClick={() => setSaved(false)}>
                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.OFFICIAL_EVALUATION.UNCOMPLETED_CONFIRMATION_DESC_LINK')}
            </a>
        </div>
    );

    /**
     * Return Save if all sections are completed
     * Return Save and Complete if one of the section is not completed
     */
    const getSaveButtonLabel = () => {
        if (allCompleted) {
            return LocaleService.t('TRADE_IN.BUTTONS.SAVE');
        }
        return LocaleService.t('TRADE_IN.BUTTONS.SAVE_AND_COMPLETE_LATER');
    };

    const SaveButton = ({ onSave }) => (
        <section className="clearfix vehicle-buttons-container" data-test="trade-in.vehicle-form.submit">
            <div className="vehicle-buttons right">
                <ActionButton next noPrefix noArrow onClick={readOnly ? onClose : onSave} loading={loading}>
                    {readOnly ? LocaleService.t('TRADE_IN.BUTTONS.DONE') : getSaveButtonLabel()}
                </ActionButton>
            </div>
        </section>
    );

    const handleSectionSave = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_NEXT_BTN());
        // history
        // susDamage
        // repairsRequired
        // registration
        // addDisclosures
    };

    const handleOpenSection = (section) => {
        switch (section) {
            case SECTION_LABEL.CAR_STORY:
                AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_CAR_STORY());
                break;
            case SECTION_LABEL.SUSTAINED_DAMAGE:
                AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_SUSTAINED_DAMAGE());
                break;
            case SECTION_LABEL.REPAIRS_REQUIRED:
                AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_REPAIRS_REQUIRED());
                break;
            case SECTION_LABEL.REGISTRATION:
                AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_REGISTRATION());
                break;
            case SECTION_LABEL.ADDITIONAL_DISCLOSURES:
                AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_ADDITIONAL_DISCLOSURES());
                break;
            default:
                break;
        }
    };

    const handleAnswerChange = (questionLabel) => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_OFFICIAL_QUESTION_ANSWER(questionLabel));
    };

    const handleConfirmationModalCancel = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_CANCEL_BTN());
    };

    const handleConfirmationModalConfirm = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_CONFIRM_BTN());
    };

    const handleConfirmationModalOpen = () => {
        AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_INCOMPLETE_CONFIRMATION_FORM());
    };

    return (
        <div className="vehicle-condition-form-360">
            {saved ? (
                <ConfirmationMessageTrade360
                    title={confirmationTitle}
                    description={confirmationDescription}
                    closeText={closeText}
                    onClose={onClose}
                    icon={isEvaluationCompleted() ? 'success' : 'warning'}
                    confirmationType={
                        isEvaluationCompleted()
                            ? CONFIRMATION_TYPE.OFFICIAL_COMPLETED
                            : CONFIRMATION_TYPE.OFFICIAL_PARTIAL
                    }
                >
                    {!controlledAccountTradeInVehicle?.appointmentDate && (
                        <TradeInCta
                            appointmentDate={controlledAccountTradeInVehicle?.appointmentDate}
                            onMakeAppointment={onMakeAppointment}
                        />
                    )}
                </ConfirmationMessageTrade360>
            ) : (
                <>
                    <div className={`${WEB_CSS_CLASSNAMES.HEADING2} vehicle-condition-form-360--header--title`}>
                        {readOnly
                            ? LocaleService.t('TRADE_IN.CTA.OFFICIAL.VIEW_ONLY_TITLE')
                            : LocaleService.t('TRADE_IN.CTA.OFFICIAL.LOGGED_IN_TITLE')}
                        {AccountService.isAuthenticated() &&
                            tradeInVehicle?.creationDate && (
                                <div className={WEB_CSS_CLASSNAMES.BODY2}>
                                    {LocaleService.t('TRADE_IN.CREATED_ON', { date: tradeInVehicle.creationDate })}
                                </div>
                            )}
                    </div>
                    {error && <ErrorMessage />}
                    <div className="vehicle-condition-form-360--header--vehicles">
                        {controlledAccountTradeInVehicle.officialAmount > 0 && (
                            <Vehicle.TradeInOfficialValue
                                amount={controlledAccountTradeInVehicle.officialAmount}
                                expiration={controlledAccountTradeInVehicle.officialAmountExp}
                                customClassCSS="step-section"
                                showIcon
                            />
                        )}
                        <VehicleTypeLabel
                            vehicleLabel={LocaleService.t('TRADE_IN.TITLES.YOUR_TRADEIN_VEHICLE')}
                            vehicleInfo={tradeVehicleName}
                            isVehicleBold
                        >
                            <div className="link" onClick={onPreviousStep}>
                                {LocaleService.t('TRADE_IN.BUTTONS.DETAILS')}
                            </div>
                        </VehicleTypeLabel>
                        <VehicleTypeLabel
                            vehicleLabel={LocaleService.t('TRADE_IN.TITLES.VEHICLE_OF_INTEREST')}
                            vehicleInfo={quoteVehicleInfo}
                        />
                        <div className="vehicle-condition-form-360--header--complete-later">
                            {LocaleService.t('TRADE_IN.OFFICIALEVALUATION.COMPLETE_LATER')}
                        </div>
                    </div>
                    <div className="vehicle-condition-form-360--report">
                        <DeskingConditionForm
                            initialAnswers={controlledAccountTradeInVehicle?.evaluationQuestion || {}}
                            lang={LocaleService.getSimpleLocale()}
                            onSave={handleReportSave}
                            onChange={onChange}
                            alwaysExpanded
                            sectionCollapsable
                            noHeader
                            validationFields="allRequired"
                            readOnly={readOnly}
                            config={config}
                            onAllSectionsCompleted={() => setAllCompleted(true)}
                            onSectionSave={(section) => handleSectionSave(section)}
                            onSetSectionOpen={(section) => handleOpenSection(section)}
                            onModalCancel={handleConfirmationModalCancel}
                            onModalConfirm={handleConfirmationModalConfirm}
                            onAnswerChange={(questionLabel) => handleAnswerChange(questionLabel)}
                            onConfirmationModalOpen={handleConfirmationModalOpen}
                        >
                            {({ handleSaveTradeIn }) => (
                                <section
                                    className="clearfix vehicle-buttons-container"
                                    data-test="trade-in.vehicle-form.submit"
                                >
                                    <div className="vehicle-buttons right">
                                        <ActionButton
                                            next
                                            noPrefix
                                            noArrow
                                            onClick={readOnly ? onClose : handleSaveTradeIn}
                                            loading={loading}
                                        >
                                            {getSaveButtonLabel()}
                                        </ActionButton>
                                    </div>
                                </section>
                            )}
                        </DeskingConditionForm>
                        {readOnly && <SaveButton />}
                    </div>
                </>
            )}
        </div>
    );
};
OfficialEvaluation.defaultProps = {
    accountTradeInVehicle: new Vehicle.TradeInVehicleModel(),
    onChange: () => {},
    config: {},
};

OfficialEvaluation.propTypes = {
    tradeInVehicle: PropTypes.object,
    accountTradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel), // trade vehicle in garage (from Redux)
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
    onClose: PropTypes.func,
    onChange: PropTypes.func,
    closeText: PropTypes.string,
    onPreviousStep: PropTypes.func.isRequired,
    config: PropTypes.object,
};

OfficialEvaluation.displayName = 'OfficialEvaluation';

function mapStateToProps(state) {
    return {
        accountTradeInVehicle: state.tradeInVehicle,
    };
}

export default connect(
    mapStateToProps,
    null
)(OfficialEvaluation);

export { OfficialEvaluation };
